import React, { useEffect } from "react";
import { SearchModel } from "types/qbank/searchModel";
import { Loader } from "components/ui";
import { useFilter } from "hooks/filter";
import { useApi } from "hooks/api";
import { useInfiniteQuery, useQuery } from "react-query";
import { Box, List, Typography, Grid } from "@mui/material";
import DetailDialog from "./detailModal/DetailDialog";
import { useMediaModal } from "hooks/mediaModal";
import MediaCard from "./MediaCard";
import MediaList from "./MediaList";

export default function SearchResult() {
  const { modal } = useMediaModal();
  const { search, getDeploymentSite } = useApi();
  const { searchState, contentView } = useFilter();
  const _searchLimit = 40;

  const deploymentSiteData = useQuery(
    ["getDeploymentSite", searchState.currentCategory.deploymentSiteId],
    () =>
      getDeploymentSite(parseInt(searchState.currentCategory.deploymentSiteId)),
    {
      staleTime: Infinity,
      retry: false,
    }
  );

  const doSearch = ({ pageParam = 0 }: any) => {
    let searchParams: SearchModel = {
      limit: _searchLimit,
      offset: pageParam,
      ...searchState.search,
    };

    if (searchParams.sortFields) {
      // @ts-ignore We need to format sortFields for the API
      searchParams.sortFields = searchParams.sortFields.map((searchSort) =>
        // @ts-ignore We need to format sortFields for the API
        [searchSort.sortField, searchSort.sortDirection].join(".")
      );
    }

    return search(searchParams);
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery(["search", searchState.search], doSearch, {
    retry: 0,
    staleTime: 50000,
    getNextPageParam: (lastPage, pages) => {
      return lastPage.limit + lastPage.offset;
    },
  });

  useEffect(() => {
    if (data !== undefined) {
      const _windowHeight = window.innerHeight * 1.5;
      const lastPage = data.pages[data.pages.length - 1];
      const hasMore = lastPage.offset + lastPage.limit < lastPage.totalHits;

      function handleScroll(e: Event) {
        const scrollElement = (e.target as HTMLDocument)
          .scrollingElement as HTMLElement;
        if (
          scrollElement.scrollTop >
            scrollElement.offsetHeight - _windowHeight &&
          hasMore &&
          !isFetchingNextPage
        ) {
          fetchNextPage();
        }
      }
      window.addEventListener("scroll", handleScroll);

      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [data, fetchNextPage, hasNextPage, isFetchingNextPage]);

  if (
    isLoading ||
    isError ||
    !data ||
    deploymentSiteData.isLoading ||
    deploymentSiteData.isError ||
    !deploymentSiteData.data
  ) {
    return <Loader />;
  }

  return (
    <>
      {data.pages.map((page, i) => (
        <React.Fragment key={i}>
          {page.totalHits === 0 ? (
            <Box mt={2} sx={{ flexGrow: 1 }}>
              <Typography variant="h4" textAlign="center">
                No results!
              </Typography>
            </Box>
          ) : (
            <>
              {contentView === "grid" ? (
                <Box mt={2} sx={{ flexGrow: 1 }}>
                  <Grid container spacing={2}>
                    {page.results?.map((item) => (
                      <MediaCard
                        key={item.mediaId}
                        media={item}
                        deploymentSite={deploymentSiteData.data}
                      />
                    ))}
                  </Grid>
                </Box>
              ) : (
                <List
                  sx={{
                    width: "100%",
                    bgcolor: "background.paper",
                  }}
                >
                  {page.results?.map((item) => (
                    <MediaList
                      key={item.mediaId}
                      media={item}
                      deploymentSite={deploymentSiteData.data}
                    />
                  ))}
                </List>
              )}
            </>
          )}
        </React.Fragment>
      ))}

      {modal.open && <DetailDialog deploymentSite={deploymentSiteData.data} />}
    </>
  );
}
